'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getTicketIdByExternalId } from '@/server-functions/csForm/getTicketIdByExternalId';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import ConfirmationPageChat from '../CustomerSupportForm/components/ConfirmationPageChat';
import { CsChannelHandoverTile } from './types/CsChannelHandoverTile';
import ConfirmationPagePhone from '../CustomerSupportForm/components/ConfirmationPagePhone';

type Props = {
  tile: CsChannelHandoverTile;
  trackingPrefix?: string;
};

export default function CsChannelHandover({ tile, trackingPrefix }: Readonly<Props>) {
  const searchParams = useSearchParams();
  const channel = searchParams.get('channel');
  const token = searchParams.get('token') ?? undefined;

  const [ticketId, setTicketId] = useState<number | null>(null);
  const [resolving, setResolving] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      setResolving(false);
      return () => {};
    }
    // wait 0.5 sec before resolving the ticket id (gives a nicer UX)
    let mounted = true;

    // Reset ticket id when token changes so stale id isn't shown while resolving
    setTicketId(null);

    // Retry when id === null. We'll do a few attempts with delays between them.
    const maxAttempts = 2;
    const initialDelayMs = 500; // initial wait for UX
    const retryDelayMs = 700; // delay between retries

    (async () => {
      try {
        // initial delay
        await new Promise((res) => { setTimeout(res, initialDelayMs); });

        let attempt = 0;
        while (mounted && attempt < maxAttempts) {
          // eslint-disable-next-line no-await-in-loop
          const id = await getTicketIdByExternalId(token);

          if (!mounted) { break; }

          if (id != null) {
            setTicketId(id);
            break;
          }

          attempt += 1;

          if (attempt < maxAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => { setTimeout(res, retryDelayMs); });
          }
        }
      } finally {
        if (mounted) {
          setResolving(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token]);

  if (resolving) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="thank-you-loading"
      >
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (channel === 'voice') {
    return (
      <ConfirmationPagePhone
        ticketId={ticketId}
        hotlineAvailabilityNotice={tile['voice-description']?.html?.[0]}
        trackingPrefix={trackingPrefix}
      />
    );
  }

  if (channel === 'chat') {
    return (
      <ConfirmationPageChat
        ticketId={ticketId}
        hotlineAvailabilityNotice={tile['chat-description']?.html?.[0]}
        trackingPrefix={trackingPrefix}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 pb-20 pt-12 text-center md:pt-16">
      <p>Please select a channel to continue.</p>
    </div>
  );
}
